import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Spinner
} from 'reactstrap';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { getAuthHeaders } from '../../helpers/utils';

const GROUPS_ENDPOINT = `${apiBase}/p-admin/review.php?action=groups`;
const GROUP_ENDPOINT = `${apiBase}/p-admin/review.php?action=group`;
const TOGGLE_ENDPOINT = `${apiBase}/p-admin/review.php?action=toggle`;

const readGroupId = search => {
  const params = new URLSearchParams(search || '');
  return (params.get('guruh_id') || '').trim();
};

const getFacultyKey = faculty => faculty.code || faculty.name;

const stateMeta = archive => {
  if (archive === 0) {
    return {
      label: 'Qatnashadi',
      badgeColor: 'success',
      className: 'psi-review-state psi-review-state--success'
    };
  }

  if (archive === 1) {
    return {
      label: 'Cheklangan',
      badgeColor: 'danger',
      className: 'psi-review-state psi-review-state--danger'
    };
  }

  return {
    label: 'Tanlanmagan',
    badgeColor: 'warning',
    className: 'psi-review-state psi-review-state--warning'
  };
};

const ReviewControl = ({ history, location }) => {
  const [faculties, setFaculties] = useState([]);
  const [groupData, setGroupData] = useState(null);
  const [loadingFaculties, setLoadingFaculties] = useState(true);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [groupSearch, setGroupSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [banner, setBanner] = useState(null);
  const [notFoundMessage, setNotFoundMessage] = useState('');
  const [activePeriodLabel, setActivePeriodLabel] = useState('');
  const [expandedFaculties, setExpandedFaculties] = useState({});

  const groupId = readGroupId(location.search);

  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  const changeGroup = groupName => {
    const search = groupName ? `?guruh_id=${encodeURIComponent(groupName)}` : '';
    history.push({
      pathname: '/tekshirish',
      search
    });
  };

  useEffect(() => {
    let active = true;

    const fetchFaculties = async () => {
      setLoadingFaculties(true);

      try {
        const response = await axios.get(GROUPS_ENDPOINT, requestConfig);
        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setFaculties(response.data.faculties || []);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Guruhlar yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Guruhlar ro‘yxatini yuklashda xatolik yuz berdi.';

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingFaculties(false);
        }
      }
    };

    fetchFaculties();

    return () => {
      active = false;
    };
  }, [requestConfig]);

  useEffect(() => {
    let active = true;

    if (!groupId) {
      setGroupData(null);
      setLoadingGroup(false);
      setNotFoundMessage('');
      return undefined;
    }

    const fetchGroup = async () => {
      setLoadingGroup(true);
      setNotFoundMessage('');

      try {
        const response = await axios.get(GROUP_ENDPOINT, {
          ...requestConfig,
          params: {
            guruh_id: groupId
          }
        });

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setGroupData(response.data.data || null);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Tekshirish ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const status = error.response && error.response.status;
        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Tekshirish ma`lumotlarini yuklashda xatolik yuz berdi.';

        setGroupData(null);

        if (status === 404) {
          setNotFoundMessage(message);
          return;
        }

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingGroup(false);
        }
      }
    };

    fetchGroup();

    return () => {
      active = false;
    };
  }, [groupId, requestConfig]);

  const toggleStudent = async (student, archive) => {
    setBusyAction({
      talabaId: student.talaba_id,
      archive
    });
    setBanner(null);

    try {
      const response = await axios.post(
        TOGGLE_ENDPOINT,
        {
          talaba_id: student.talaba_id,
          archive
        },
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.success) {
        setBusyAction(null);
        setGroupData(response.data.data || null);
        setActivePeriodLabel(formatSelectedPeriod(response.data.period));
        return;
      }

      throw new Error((response.data && response.data.message) || 'Saqlashda xatolik yuz berdi.');
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        'Talaba holatini saqlashda xatolik yuz berdi.';

      setBusyAction(null);
      setBanner({ color: 'danger', text: message });
      toast.error(message);
    }
  };

  const filteredFaculties = useMemo(() => {
    const search = groupSearch.trim().toLowerCase();

    return faculties
      .map(faculty => {
        const groups = (faculty.groups || []).filter(group => {
          if (!search) {
            return true;
          }

          return group.name.toLowerCase().includes(search);
        });

        return {
          ...faculty,
          groups
        };
      })
      .filter(faculty => faculty.groups.length > 0);
  }, [faculties, groupSearch]);

  useEffect(() => {
    if (!filteredFaculties.length) {
      return;
    }

    setExpandedFaculties(prev => {
      const next = { ...prev };

      if (groupId) {
        const selectedFaculty = filteredFaculties.find(faculty =>
          (faculty.groups || []).some(group => group.name === groupId)
        );

        if (selectedFaculty) {
          next[getFacultyKey(selectedFaculty)] = true;
          return next;
        }
      }

      if (!Object.values(next).some(Boolean)) {
        next[getFacultyKey(filteredFaculties[0])] = true;
      }

      return next;
    });
  }, [filteredFaculties, groupId]);

  const toggleFaculty = facultyKey => {
    setExpandedFaculties(prev => ({
      ...prev,
      [facultyKey]: !prev[facultyKey]
    }));
  };

  const filteredStudents = useMemo(() => {
    const students = (groupData && groupData.students) || [];
    const search = studentSearch.trim().toLowerCase();

    return students.filter(student => {
      const matchesSearch =
        !search ||
        student.full_name.toLowerCase().includes(search) ||
        String(student.talaba_id).includes(search) ||
        (student.passport || '').toLowerCase().includes(search);

      if (!matchesSearch) {
        return false;
      }

      if (statusFilter === 'active') {
        return student.archive === 0;
      }

      if (statusFilter === 'blocked') {
        return student.archive === 1;
      }

      if (statusFilter === 'pending') {
        return student.archive !== 0 && student.archive !== 1;
      }

      return true;
    });
  }, [groupData, statusFilter, studentSearch]);

  const columns = useMemo(
    () => [
      {
        dataField: 'order',
        text: 'T/R',
        sort: true,
        classes: 'align-middle font-weight-semi-bold'
      },
      {
        dataField: 'full_name',
        text: 'Talaba',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div className="psi-review-student">
            <div className="psi-review-student__avatar">{row.initials}</div>
            <div className="psi-review-student__copy">
              <strong>{value}</strong>
              <span>Talaba ID: {row.talaba_id}</span>
            </div>
          </div>
        )
      },
      {
        dataField: 'passport',
        text: 'Pasport',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-review-passport">{value || 'Mavjud emas'}</span>
      },
      {
        dataField: 'archive',
        text: 'Holat',
        sort: true,
        classes: 'align-middle',
        formatter: value => {
          const meta = stateMeta(value);
          return (
            <Badge pill className={meta.className}>
              {meta.label}
            </Badge>
          );
        }
      },
      {
        dataField: 'actions',
        text: 'Boshqaruv',
        classes: 'align-middle',
        headerStyle: () => ({ minWidth: '14rem' }),
        formatter: (_, row) => {
          const isBusy = busyAction && busyAction.talabaId === row.talaba_id;
          const isAllowBusy = isBusy && busyAction.archive === 0;
          const isBlockBusy = isBusy && busyAction.archive === 1;

          return (
            <div className="psi-review-actions">
              <Button
                color="light"
                size="sm"
                className={`psi-review-action-button psi-review-action-button--allow ${row.archive === 0 ? 'is-active' : ''}`}
                disabled={isBusy}
                onClick={() => toggleStudent(row, 0)}
              >
                {isAllowBusy ? (
                  <Spinner size="sm" className="psi-review-action-spinner" />
                ) : (
                  <span className="psi-review-action-icon">+</span>
                )}
                <span>Qatnashadi</span>
              </Button>
              <Button
                color="light"
                size="sm"
                className={`psi-review-action-button psi-review-action-button--block ${row.archive === 1 ? 'is-active' : ''}`}
                disabled={isBusy}
                onClick={() => toggleStudent(row, 1)}
              >
                {isBlockBusy ? (
                  <Spinner size="sm" className="psi-review-action-spinner" />
                ) : (
                  <span className="psi-review-action-icon">-</span>
                )}
                <span>Cheklash</span>
              </Button>
            </div>
          );
        }
      }
    ],
    [busyAction]
  );

  const statCards = groupData
    ? [
        {
          title: 'Jami talabalar',
          value: groupData.stats.total_students,
          tone: 'primary',
          icon: 'users',
          note: 'umumiy'
        },
        {
          title: 'Qatnashadi',
          value: groupData.stats.active_count,
          tone: 'success',
          icon: 'user-check',
          note: 'faol'
        },
        {
          title: 'Cheklangan',
          value: groupData.stats.blocked_count,
          tone: 'danger',
          icon: 'ban',
          note: 'yopiq'
        },
        {
          title: 'Tanlanmagan',
          value: groupData.stats.pending_count,
          tone: 'warning',
          icon: 'clock',
          note: 'kutilmoqda'
        }
      ]
    : [];

  return (
    <>
      <div className="psi-review-heading mb-3">
        <div>
          <div className="psi-review-heading__nav">Boshqaruv / Tekshirish paneli</div>
          <h3 className="mb-1">Tekshirish paneli</h3>
        </div>
        {activePeriodLabel || groupId ? (
          <div className="psi-heading-badges">
            {activePeriodLabel ? <Badge pill className="psi-active-period-badge">{activePeriodLabel}</Badge> : null}
            {groupId ? (
              <Badge pill className="psi-review-heading__badge">
                {groupId}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <div className="psi-review-toolbar mb-3">
        <div className="psi-review-toolbar__copy">
          <h5 className="mb-1">{groupId ? `${groupId} guruhi` : 'Guruh tanlanmagan'}</h5>
          <p className="text-600 mb-0">
            {groupId
              ? 'Talaba holatini tanlab, o‘zgarishni darhol saqlaysiz.'
              : 'Kerakli guruhni qidirib tanlang.'}
          </p>
        </div>

        <div className="psi-review-toolbar__controls">
          <Input
            value={groupSearch}
            onChange={({ target }) => setGroupSearch(target.value)}
            placeholder="Guruh qidirish"
            className="psi-review-search"
          />
          {groupId ? (
            <Button color="light" className="psi-review-switch-button" onClick={() => changeGroup('')}>
              Guruhni almashtirish
            </Button>
          ) : null}
        </div>
      </div>

      {notFoundMessage ? (
        <Alert color="warning" className="mb-3">
          {notFoundMessage}
        </Alert>
      ) : null}

      <Row className="mb-3 psi-review-layout">
        <Col xl="4" className="mb-3 mb-xl-0">
          <Card className="border-0 shadow-sm h-100 psi-review-sidebar-card">
            <CardBody className="psi-review-sidebar-card__body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Fakultetlar va guruhlar</h5>
                {loadingFaculties ? <Spinner size="sm" color="primary" /> : <Badge color="light">{filteredFaculties.length} ta</Badge>}
              </div>

              <div className="psi-review-group-list">
                {loadingFaculties ? (
                  <div className="text-center text-500 py-4">Guruhlar yuklanmoqda...</div>
                ) : filteredFaculties.length > 0 ? (
                  filteredFaculties.map(faculty => (
                    <div key={getFacultyKey(faculty)} className="psi-review-faculty">
                      <button
                        type="button"
                        className={`psi-review-faculty__header ${expandedFaculties[getFacultyKey(faculty)] ? 'is-open' : ''}`}
                        onClick={() => toggleFaculty(getFacultyKey(faculty))}
                      >
                        <div className="psi-review-faculty__copy">
                          <strong>{faculty.name}</strong>
                          <span>{faculty.groups.length} ta guruh</span>
                        </div>
                        <span className="psi-review-faculty__toggle" aria-hidden="true">
                          {expandedFaculties[getFacultyKey(faculty)] ? '−' : '+'}
                        </span>
                      </button>
                      {expandedFaculties[getFacultyKey(faculty)] ? (
                        <div className="psi-review-faculty__groups">
                          {faculty.groups.map(group => (
                            <button
                              key={group.name}
                              type="button"
                              className={`psi-review-group-button ${groupId === group.name ? 'is-active' : ''}`}
                              onClick={() => changeGroup(group.name)}
                            >
                              <span>{group.name}</span>
                              <small>{group.student_count} talaba</small>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-500 py-4">Mos guruh topilmadi.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl="8" className="psi-review-main-col">
          {loadingGroup ? (
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex align-items-center justify-content-center py-6">
                <div className="text-center">
                  <Spinner color="primary" className="mb-3" />
                  <div className="text-600">Tekshirish ma`lumotlari yuklanmoqda...</div>
                </div>
              </CardBody>
            </Card>
          ) : groupData ? (
            <div className="psi-review-main-shell">
              <Row className="mb-3 psi-review-stats-row">
                {statCards.map(card => (
                  <Col md="6" xl="3" key={card.title} className="mb-3">
                    <Card className={`border-0 shadow-sm h-100 psi-review-stat psi-review-stat--${card.tone}`}>
                      <CardBody>
                        <div className="psi-review-stat__top">
                          <div className="psi-review-stat__icon">
                            <FontAwesomeIcon icon={card.icon} />
                          </div>
                          <div className="psi-review-stat__chip">{card.note}</div>
                        </div>
                        <div className="psi-review-stat__value">{card.value}</div>
                        <div className="psi-review-stat__label">{card.title}</div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Card className="border-0 shadow-sm psi-review-main-card">
                <CardBody className="psi-review-main-card__body">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3 psi-review-main-card__header">
                    <div className="mb-3 mb-lg-0">
                      <h5 className="mb-1">{groupData.group} guruh ro‘yxati</h5>
                      <p className="text-600 mb-0">Talaba ustidagi tugmalar orqali testga kirishni boshqaring.</p>
                    </div>
                    <div className="d-flex flex-column flex-sm-row">
                      <Input
                        value={studentSearch}
                        onChange={({ target }) => setStudentSearch(target.value)}
                        placeholder="Talaba qidirish"
                        className="psi-review-search"
                      />
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={({ target }) => setStatusFilter(target.value)}
                        className="ml-sm-2 mt-2 mt-sm-0 psi-review-filter"
                      >
                        <option value="all">Barcha holatlar</option>
                        <option value="active">Qatnashadi</option>
                        <option value="blocked">Cheklangan</option>
                        <option value="pending">Tanlanmagan</option>
                      </Input>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                    <Badge color="light" pill className="px-3 py-2">
                      {filteredStudents.length} ta yozuv
                    </Badge>
                    <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mt-2 mt-sm-0" />
                  </div>

                  <div className="psi-review-table-scroll">
                    <MedDataTable
                      data={filteredStudents}
                      columns={columns}
                      keyField="talaba_id"
                      pageSize={pageSize}
                      paginationResetKey={`${groupData.group}-${statusFilter}-${studentSearch}`}
                      noDataIndication={<div className="text-center py-4 text-500">Bu filtr bo‘yicha talaba topilmadi.</div>}
                      tableClasses="table table-hover fs--1 mb-0 med-dashboard-table psi-review-table"
                      headerClasses="bg-200 text-900"
                      wrapperClasses="table-responsive psi-review-table-wrap"
                    />
                  </div>
                </CardBody>
              </Card>
            </div>
          ) : (
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex align-items-center justify-content-center py-6">
                <div className="text-center">
                  <div className="psi-review-empty-icon mb-3">TR</div>
                  <h5 className="mb-2">Tekshirish guruhi tanlanmagan</h5>
                  <p className="text-600 mb-0">Chap tomondan kerakli guruhni tanlang, ro‘yxat shu yerda ochiladi.</p>
                </div>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default ReviewControl;
